import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Image as ImageIcon,
  Youtube,
  Users,
  Building2,
  Calendar,
  Eye,
  EyeOff,
  Upload,
  ExternalLink,
  LayoutDashboard,
  Bell,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const DISPLAY_TYPES = {
  header: { label: 'Bandeau Header', icon: LayoutDashboard, description: 'En haut de toutes les pages' },
  popup: { label: 'Popup Connexion', icon: Bell, description: 'Apparaît à la connexion' },
  carousel: { label: 'Carrousel Flash Infos', icon: Layers, description: 'Section sur le dashboard' }
};

const TARGET_OPTIONS = [
  { value: 'all', label: 'Tout le monde', icon: Users },
  { value: 'clients', label: 'Clients uniquement', icon: Building2 },
  { value: 'members', label: 'Membres uniquement', icon: Users }
];

const AnnouncementsAdminPage = () => {
  const { theme } = useTheme();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    display_type: 'header',
    media_type: 'none', // none, image, video
    media_url: '',
    background_color: '#1a365d',
    text_color: '#ffffff',
    overlay_color: '#000000',
    overlay_opacity: 0.5,
    banner_height: 'compact', // compact, medium, large
    target_audience: ['all'],
    start_date: '',
    end_date: '',
    action_button_text: '',
    action_button_url: '',
    priority: 0,
    enabled: true
  });
  
  // Banner height options
  const BANNER_HEIGHTS = {
    compact: { label: 'Compact (56px)', size: '1920 × 56px' },
    medium: { label: 'Moyen (96px)', size: '1920 × 96px' },
    large: { label: 'Grand (160px)', size: '1920 × 160px' }
  };

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await api.get('/settings/announcements/admin');
      setAnnouncements(res.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      display_type: 'header',
      media_type: 'none',
      media_url: '',
      background_color: '#1a365d',
      text_color: '#ffffff',
      overlay_color: '#000000',
      overlay_opacity: 0.5,
      banner_height: 'compact',
      target_audience: ['all'],
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      action_button_text: '',
      action_button_url: '',
      priority: 0,
      enabled: true
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingAnnouncement(null);
    setShowDialog(true);
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      display_type: announcement.display_type || 'header',
      media_type: announcement.media_type || 'none',
      media_url: announcement.media_url || '',
      background_color: announcement.background_color || '#1a365d',
      text_color: announcement.text_color || '#ffffff',
      overlay_color: announcement.overlay_color || '#000000',
      overlay_opacity: announcement.overlay_opacity ?? 0.5,
      banner_height: announcement.banner_height || 'compact',
      target_audience: announcement.target_audience || ['all'],
      start_date: announcement.start_date?.split('T')[0] || '',
      end_date: announcement.end_date?.split('T')[0] || '',
      action_button_text: announcement.action_button_text || '',
      action_button_url: announcement.action_button_url || '',
      priority: announcement.priority || 0,
      enabled: announcement.enabled !== false
    });
    setShowDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await api.post('/settings/announcements/upload-image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, media_url: res.data.url, media_type: 'image' }));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date + 'T23:59:59').toISOString(),
        media_type: formData.media_type === 'none' ? null : formData.media_type,
        media_url: formData.media_type === 'none' ? null : formData.media_url
      };
      
      if (editingAnnouncement) {
        await api.put(`/settings/announcements/${editingAnnouncement.id}`, payload);
        toast.success('Annonce mise à jour');
      } else {
        await api.post('/settings/announcements', payload);
        toast.success('Annonce créée');
      }
      
      setShowDialog(false);
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    
    try {
      await api.delete(`/settings/announcements/${id}`);
      toast.success('Annonce supprimée');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleEnabled = async (announcement) => {
    try {
      await api.put(`/settings/announcements/${announcement.id}`, {
        ...announcement,
        enabled: !announcement.enabled
      });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const isActive = (announcement) => {
    if (!announcement.enabled) return false;
    const now = new Date();
    const start = new Date(announcement.start_date);
    const end = new Date(announcement.end_date);
    return now >= start && now <= end;
  };

  const getStatusBadge = (announcement) => {
    if (!announcement.enabled) {
      return <Badge variant="outline" className="text-slate-500">Désactivée</Badge>;
    }
    const now = new Date();
    const start = new Date(announcement.start_date);
    const end = new Date(announcement.end_date);
    
    if (now < start) {
      return <Badge className="bg-blue-100 text-blue-700">Planifiée</Badge>;
    }
    if (now > end) {
      return <Badge className="bg-slate-100 text-slate-600">Expirée</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <Megaphone className="w-6 h-6" />
              Gestion des Annonces
            </h1>
            <p className="text-sm text-slate-500">Bannières, popups et flash infos</p>
          </div>
          <Button onClick={openCreateDialog} style={{ backgroundColor: theme.colors?.accent }}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Annonce
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(DISPLAY_TYPES).map(([key, config]) => {
            const count = announcements.filter(a => a.display_type === key && isActive(a)).length;
            return (
              <Card key={key}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <config.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium">{config.label}</p>
                    <p className="text-sm text-slate-500">{count} active(s)</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Announcements List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Toutes les annonces</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Megaphone className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucune annonce</p>
              </div>
            ) : (
              <div className="space-y-4">
                {announcements.map((announcement) => {
                  const displayType = DISPLAY_TYPES[announcement.display_type] || DISPLAY_TYPES.header;
                  
                  return (
                    <div
                      key={announcement.id}
                      className={cn(
                        "border rounded-lg p-4 transition-all",
                        isActive(announcement) ? "border-green-200 bg-green-50/30" : "border-slate-200"
                      )}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div 
                            className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                            style={{ backgroundColor: announcement.background_color }}
                          >
                            {announcement.media_type === 'image' && announcement.media_url ? (
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}${announcement.media_url}`}
                                alt=""
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <displayType.icon className="w-6 h-6" />
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{announcement.title}</h3>
                              {getStatusBadge(announcement)}
                              <Badge variant="outline" className="text-xs">
                                <displayType.icon className="w-3 h-3 mr-1" />
                                {displayType.label}
                              </Badge>
                            </div>
                            
                            {announcement.content && (
                              <p className="text-sm text-slate-600 mb-2">{announcement.content}</p>
                            )}
                            
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(announcement.start_date).toLocaleDateString('fr-FR')} - {new Date(announcement.end_date).toLocaleDateString('fr-FR')}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {announcement.target_audience?.includes('all') ? 'Tous' : 
                                 announcement.target_audience?.includes('clients') ? 'Clients' : 'Membres'}
                              </span>
                              {announcement.media_type === 'video_youtube' && (
                                <span className="flex items-center gap-1">
                                  <Youtube className="w-3 h-3 text-red-500" />
                                  Vidéo YouTube
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={announcement.enabled}
                            onCheckedChange={() => toggleEnabled(announcement)}
                          />
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(announcement)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(announcement.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Modifier l\'annonce' : 'Nouvelle annonce'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Title */}
            <div>
              <Label>Titre *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Titre de l'annonce"
              />
            </div>
            
            {/* Content */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Description (optionnel)"
                rows={3}
              />
            </div>
            
            {/* Display Type */}
            <div>
              <Label>Type d'affichage</Label>
              <Select
                value={formData.display_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, display_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DISPLAY_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {DISPLAY_TYPES[formData.display_type]?.description}
              </p>
            </div>
            
            {/* Media */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Média de fond</Label>
              
              <Select
                value={formData.media_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, media_type: value, media_url: value === 'none' ? '' : prev.media_url }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Couleur unie (pas de média)</SelectItem>
                  <SelectItem value="image">Image de fond</SelectItem>
                  <SelectItem value="video">Vidéo de fond (autoplay, muet)</SelectItem>
                </SelectContent>
              </Select>
              
              {formData.media_type === 'image' && (
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex gap-2">
                    <Input
                      value={formData.media_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, media_url: e.target.value }))}
                      placeholder="URL de l'image"
                      className="flex-1"
                    />
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                      <Button type="button" variant="outline" disabled={uploading} asChild>
                        <span>
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        </span>
                      </Button>
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    📐 Taille recommandée: <strong>{BANNER_HEIGHTS[formData.banner_height]?.size || '1920 × 56px'}</strong>
                  </p>
                  {formData.media_url && (
                    <img 
                      src={formData.media_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${formData.media_url}` : formData.media_url}
                      alt="Preview"
                      className="max-h-40 rounded-lg border"
                    />
                  )}
                </div>
              )}
              
              {formData.media_type === 'video' && (
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex gap-2">
                    <Input
                      value={formData.media_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, media_url: e.target.value }))}
                      placeholder="URL de la vidéo (MP4, WebM)"
                      className="flex-1"
                    />
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept="video/mp4,video/webm" 
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setUploading(true);
                          try {
                            const formDataUpload = new FormData();
                            formDataUpload.append('file', file);
                            const res = await api.post('/settings/announcements/upload-image', formDataUpload, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setFormData(prev => ({ ...prev, media_url: res.data.url, media_type: 'video' }));
                            toast.success('Vidéo uploadée');
                          } catch (error) {
                            toast.error('Erreur lors de l\'upload');
                          } finally {
                            setUploading(false);
                          }
                        }} 
                        className="hidden" 
                      />
                      <Button type="button" variant="outline" disabled={uploading} asChild>
                        <span>
                          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        </span>
                      </Button>
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    🎬 La vidéo sera en autoplay et muet. Format MP4 ou WebM recommandé.<br/>
                    📐 Taille recommandée: <strong>{BANNER_HEIGHTS[formData.banner_height]?.size || '1920 × 56px'}</strong>
                  </p>
                  {formData.media_url && (
                    <video 
                      src={formData.media_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${formData.media_url}` : formData.media_url}
                      className="max-h-40 rounded-lg border"
                      controls
                      muted
                    />
                  )}
                </div>
              )}
            </div>
            
            {/* Banner Height */}
            {formData.display_type === 'header' && (
              <div>
                <Label>Hauteur du bandeau</Label>
                <Select
                  value={formData.banner_height}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, banner_height: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BANNER_HEIGHTS).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Colors & Overlay */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Couleurs</Label>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Couleur de fond</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={formData.background_color}
                      onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border"
                    />
                    <Input
                      value={formData.background_color}
                      onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Couleur du texte</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={formData.text_color}
                      onChange={(e) => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border"
                    />
                    <Input
                      value={formData.text_color}
                      onChange={(e) => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Overlay settings - only when media is set */}
              {(formData.media_type === 'image' || formData.media_type === 'video') && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <Label className="text-sm font-medium">Overlay sur le média</Label>
                  <p className="text-xs text-slate-500 -mt-2">
                    L'overlay est une couche de couleur semi-transparente au-dessus de l'image/vidéo pour améliorer la lisibilité du texte.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Couleur de l'overlay</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={formData.overlay_color}
                          onChange={(e) => setFormData(prev => ({ ...prev, overlay_color: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer border"
                        />
                        <Input
                          value={formData.overlay_color}
                          onChange={(e) => setFormData(prev => ({ ...prev, overlay_color: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Opacité: {Math.round(formData.overlay_opacity * 100)}%</Label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={formData.overlay_opacity}
                        onChange={(e) => setFormData(prev => ({ ...prev, overlay_opacity: parseFloat(e.target.value) }))}
                        className="w-full mt-3"
                      />
                      <div className="flex justify-between text-xs text-slate-400 mt-1">
                        <span>Transparent</span>
                        <span>Opaque</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Target Audience */}
            <div>
              <Label>Cible</Label>
              <Select
                value={formData.target_audience[0]}
                onValueChange={(value) => setFormData(prev => ({ ...prev, target_audience: [value] }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TARGET_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <opt.icon className="w-4 h-4" />
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Action Button */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Texte du bouton (optionnel)</Label>
                <Input
                  value={formData.action_button_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, action_button_text: e.target.value }))}
                  placeholder="En savoir plus"
                />
              </div>
              <div>
                <Label>Lien du bouton</Label>
                <Input
                  value={formData.action_button_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, action_button_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>
            
            {/* Priority */}
            <div>
              <Label>Priorité (plus élevé = plus important)</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
              />
            </div>
            
            {/* Preview */}
            <div>
              <Label>Aperçu</Label>
              <div 
                className="mt-2 rounded-lg p-4 text-center"
                style={{ 
                  backgroundColor: formData.background_color,
                  color: formData.text_color
                }}
              >
                <h3 className="font-bold">{formData.title || 'Titre'}</h3>
                {formData.content && <p className="text-sm opacity-90">{formData.content}</p>}
                {formData.action_button_text && (
                  <Button size="sm" variant="secondary" className="mt-2">
                    {formData.action_button_text}
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingAnnouncement ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default AnnouncementsAdminPage;
